import { type EffectCallback, useEffect, useRef } from 'react';

import { isDefined } from '@enterprise/core';

/**
 * A custom React hook that runs the provided effect callback only once, after the component mounts.
 *
 * @param effect - An optional effect callback function to execute after the component mounts.
 * @returns A boolean indicating whether the component has mounted.
 *
 * @remarks
 * - The effect will only run once, similar to `componentDidMount` in class components.
 * - If no effect is provided, nothing will be executed on mount.
 *
 * @example
 * ```tsx
 * useDidMount(() => {
 *   console.log('Component mounted!');
 * });
 * ```
 */
export const useDidMount = (effect?: EffectCallback) => {
  const ref = useRef<EffectCallback>(undefined);
  const isMounted = useRef(false);

  useEffect(() => {
    ref.current = effect;
  }, [effect]);

  useEffect(() => {
    const cb = ref.current;

    if (isDefined(cb) && !isMounted.current) {
      isMounted.current = true;
      cb();
    }
  }, []);

  return isMounted.current;
};
